import { createContext, FC, useCallback, useEffect, useRef, useState } from "react";
import { ReactElementProps } from "../../types";
import { LoadFromUrlPayload, PlaybackContextType, PlaybackState } from "../../types/playback-provider";
import { useConferenceContext } from "../../hooks/useConferenceContext/useConferenceContext";
import { BackgroundAudioProcessor } from "../../functions/BackgroundAudioProcessor";

export const ConferencePlaybackContext = createContext<PlaybackContextType>(null!);

export const ConferencePlaybackProvider: FC<ReactElementProps> = ({ children }) => {
	const { addAudioProcessor, removeAudioProcessor, disconnect, hasDevice } = useConferenceContext();

	const processorRef = useRef<BackgroundAudioProcessor | null>(null);
	const [isPlaying, setIsPlaying] = useState<PlaybackState>("notready");
	const pendingLoopRef = useRef<boolean>(false);

	const wireElementListeners = useCallback((el: HTMLAudioElement) => {
		const onPlay = () => {
			setIsPlaying("playing");
			console.info("[ConferencePlaybackProvider] Audio playing");
		};

		const onPause = () => {
			setIsPlaying("paused");
			console.info("[ConferencePlaybackProvider] Audio paused");
		};

		const onEnded = async () => {
			console.info("[ConferencePlaybackProvider] Audio ended; detaching processor & disconnecting conference");
			setIsPlaying("ended");

			try {
				if (processorRef.current) {
					await removeAudioProcessor(processorRef.current);
				}
			} catch (e: any) {
				console.warn("[ConferencePlaybackProvider] Failed to remove audio processor on end:", e?.message ?? e);
			}

			try {
				await disconnect();
			} catch (e: any) {
				console.warn("[ConferencePlaybackProvider] Error disconnecting conference on end:", e?.message ?? e);
			} finally {
				processorRef.current = null;
			}
		};

		const onError = () => {
			console.info("[ConferencePlaybackProvider] Audio error");
		};

		const onCanPlay = async () => {
			try {
				if (processorRef.current) {
					await processorRef.current.play();
				}
			} catch (e: any) {
				console.warn("[ConferencePlaybackProvider] Autoplay blocked or play() failed in canplay:", e?.message ?? e);
			}
		};

		el.addEventListener("play", onPlay);
		el.addEventListener("pause", onPause);
		el.addEventListener("ended", onEnded);
		el.addEventListener("error", onError);
		el.addEventListener("canplay", onCanPlay);

		return () => {
			el.removeEventListener("play", onPlay);
			el.removeEventListener("pause", onPause);
			el.removeEventListener("ended", onEnded);
			el.removeEventListener("error", onError);
			el.removeEventListener("canplay", onCanPlay);
		};
	}, [removeAudioProcessor, disconnect]);

	const attachProcessorIfPossible = useCallback(async () => {
		const processor = processorRef.current;

		if (!processor) {
			return;
		}
		if (!hasDevice) {
			return;
		}

		try {
			await addAudioProcessor(processor);
		} catch (e: any) {
			console.warn("[ConferencePlaybackProvider:attachProcessorIfPossible] addAudioProcessor failed:", e?.message ?? e);
			return;
		}

		const el = processor.getElement();
		if (el) {
			// apply pending loop setting once element exists
			try {
				el.loop = pendingLoopRef.current;
			} catch (e: any) {
				console.warn("[ConferencePlaybackProvider:attachProcessorIfPossible] Failed to set loop on element:", e?.message ?? e);
			}

			const prior = (el as any).__unwire as (() => void) | undefined;
			if (prior) {
				try {
					prior();
				} catch (e: any) {
					console.warn("[ConferencePlaybackProvider:attachProcessorIfPossible] Failed unwiring previous listeners:", e?.message ?? e);
				}
			}

			const unwire = wireElementListeners(el);
			(el as any).__unwire = unwire;

			try {
				await processor.play();
			} catch (e: any) {
				console.warn("[ConferencePlaybackProvider:attachProcessorIfPossible] Initial play() after attach failed:", e?.message ?? e);
			}
		}
	}, [hasDevice, addAudioProcessor, wireElementListeners]);

	const loadFromUrl = useCallback(async ({ url, mimeType, opts }: LoadFromUrlPayload): Promise<void> => {
		console.info(`[ConferencePlaybackProvider:loadFromUrl] URL: ${url}`);

		if (!url) {
			return;
		}

		setIsPlaying("notready");

		const topLevelType = mimeType?.split(";")[0]?.split("/")?.[0]?.toLowerCase();
		if (mimeType && topLevelType !== "audio") {
			console.warn(`[ConferencePlaybackProvider:loadFromUrl] Non-audio MIME (${mimeType}) provided; treating as audio.`);
		}

		pendingLoopRef.current = !!opts?.loop;

		// Create (or replace) processor; provider will auto-remove the old one if present
		processorRef.current = new BackgroundAudioProcessor(url);

		try {
			await attachProcessorIfPossible();
		} catch (e: any) {
			console.warn("[ConferencePlaybackProvider:loadFromUrl] attachProcessorIfPossible threw:", e?.message ?? e);
		}
	}, [attachProcessorIfPossible]);

	useEffect(() => {
		const run = async () => {
			try {
				await attachProcessorIfPossible();
			} catch (e: any) {
				console.warn("[ConferencePlaybackProvider:useEffect] Deferred attach failed:", e?.message ?? e);
			}
		};
		run();
	}, [hasDevice, attachProcessorIfPossible]);

	const togglePlayback = (): void => {
		const processor = processorRef.current;

		if (!processor) {
			return;
		}

		const el = processor.getElement();
		if (!el) {
			return;
		}

		if (isPlaying === "playing") {
			processor.pause();
		} else {
			if (isPlaying === "paused" || isPlaying === "notready") {
				processor.play().catch((e: any) => {
					console.warn("[ConferencePlaybackProvider:togglePlayback] play() failed:", e?.message ?? e);
				});
			}
		}
	};

	const adjustVolume = (volume: number): void => {
		const processor = processorRef.current;

		if (!processor) {
			return;
		}

		processor.setVolume(volume);
	};

	useEffect(() => {
		window.playbackProvider = { ...(window as any).playbackProvider, isPlaying };

		return () => {
			if (window.playbackProvider) {
				delete (window.playbackProvider as any).isPlaying;
			}
		};
	}, [isPlaying]);

	useEffect(() => {
		return () => {
			(async () => {
				try {
					const processor = processorRef.current;
					const el = processor?.getElement?.();
					if (el) {
						const unwire = (el as any).__unwire as (() => void) | undefined;
						if (unwire) {
							try {
								unwire();
							} catch (e: any) {
								console.warn("[ConferencePlaybackProvider:useEffect] Unwire listeners failed:", e?.message ?? e);
							}
						}
					}

					if (processor) {
						try {
							await removeAudioProcessor(processor);
						} catch (e: any) {
							console.warn("[ConferencePlaybackProvider:useEffect] removeAudioProcessor during cleanup failed:", e?.message ?? e);
						}
					}
				} catch (e: any) {
					console.warn("[ConferencePlaybackProvider:useEffect] Cleanup outer failed:", e?.message ?? e);
				} finally {
					processorRef.current = null;
				}
			})();
		};
	}, [removeAudioProcessor]);

	return (
		<ConferencePlaybackContext.Provider value={{ loadFromUrl, togglePlayback, adjustVolume, isPlaying }}>
			{children}
		</ConferencePlaybackContext.Provider>
	);
};
